/**
 * Scroll-linked reveal for hotspot-image blocks.
 * Sets --hotspot-reveal (0–1) from how centered and visible the image is in the viewport.
 */
(function () {
  if (customElements.get('hotspot-scroll-reveal')) return;

  /**
   * @param {DOMRectReadOnly} rect
   * @param {number} viewportHeight
   * @returns {number}
   */
  function computeRevealProgress(rect, viewportHeight) {
    if (rect.bottom <= 0 || rect.top >= viewportHeight) return 0;

    const visibleTop = Math.max(0, rect.top);
    const visibleBottom = Math.min(viewportHeight, rect.bottom);
    const visibleHeight = visibleBottom - visibleTop;

    if (visibleHeight <= 0 || rect.height <= 0) return 0;

    const elementCenter = rect.top + rect.height / 2;
    const viewportCenter = viewportHeight / 2;

    const centerRange = Math.max(viewportHeight * 0.5, rect.height * 0.25);
    const centerProgress = 1 - Math.min(1, Math.abs(elementCenter - viewportCenter) / centerRange);

    if (rect.height > viewportHeight) {
      const visibleFraction = visibleHeight / viewportHeight;
      const entryProgress = Math.min(1, visibleFraction / 0.5);
      return Math.min(1, centerProgress * entryProgress);
    }

    const visibility = visibleHeight / rect.height;
    return Math.min(1, visibility * 0.35 + centerProgress * 0.65);
  }

  class HotspotScrollReveal extends HTMLElement {
    /** @type {number | null} */
    #raf = null;

    connectedCallback() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.style.setProperty('--hotspot-reveal', '1');
        this.setAttribute('data-hotspot-reveal-active', '');
        return;
      }

      window.addEventListener('scroll', this.#scheduleUpdate, { passive: true });
      window.addEventListener('resize', this.#scheduleUpdate, { passive: true });
      this.#update();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.#scheduleUpdate);
      window.removeEventListener('resize', this.#scheduleUpdate);

      if (this.#raf !== null) {
        cancelAnimationFrame(this.#raf);
        this.#raf = null;
      }
    }

    #scheduleUpdate = () => {
      if (this.#raf !== null) return;

      this.#raf = requestAnimationFrame(() => {
        this.#raf = null;
        this.#update();
      });
    };

    #update() {
      const progress = computeRevealProgress(this.getBoundingClientRect(), window.innerHeight);
      this.style.setProperty('--hotspot-reveal', progress.toFixed(4));

      if (progress > 0.08) {
        this.setAttribute('data-hotspot-reveal-active', '');
      } else {
        this.removeAttribute('data-hotspot-reveal-active');
      }
    }
  }

  customElements.define('hotspot-scroll-reveal', HotspotScrollReveal);
})();
